export type Difficulty = "cadet" | "pilot" | "ace";

export interface Choice {
  value: number;
  key: string;
  correct: boolean;
  lane: number;
}

export interface Question {
  a: number;
  b: number;
  prompt: string;
  answer: number;
  choices: Choice[];
}

export interface QuestionConfig {
  tables: number[];
  difficulty: Difficulty;
}

export interface Mastery {
  seen: number;
  hits: number;
  misses: number;
  bestMs: number;
}

export function defaultTables(): number[] {
  return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
}

export function hardTables(): number[] {
  return [6, 7, 8, 9, 11, 12];
}

export function squareTables(): number[] {
  return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
}

function uniquePush(into: number[], value: number): void {
  if (value > 0 && !into.includes(value)) into.push(value);
}

/** Plausible wrong products: nearby facts, off-by-one, common mixups. */
export function distractors(a: number, b: number, answer: number, rng: () => number): number[] {
  const pool: number[] = [];
  uniquePush(pool, a * Math.max(1, b - 1));
  uniquePush(pool, a * Math.min(12, b + 1));
  uniquePush(pool, Math.max(1, a - 1) * b);
  uniquePush(pool, Math.min(12, a + 1) * b);
  uniquePush(pool, (a + 1) * (b - 1));
  uniquePush(pool, (a - 1) * (b + 1));
  uniquePush(pool, a + b);
  uniquePush(pool, a * 10 + b);
  uniquePush(pool, answer + 1);
  uniquePush(pool, Math.max(1, answer - 1));
  uniquePush(pool, answer + a);
  uniquePush(pool, answer - b);
  uniquePush(pool, b * b);
  uniquePush(pool, a * a);

  const filtered = pool.filter((n) => n !== answer && n > 0 && n <= 144);
  for (let guard = 0; filtered.length < 8 && guard < 40; guard++) {
    const jitter = answer + Math.floor(rng() * 11) - 5;
    if (jitter !== answer && jitter > 0) uniquePush(filtered, jitter);
  }
  return filtered;
}

function shuffle<T>(items: T[], rng: () => number): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export interface WeightedFact {
  a: number;
  b: number;
  weight: number;
}

export function buildFactBag(tables: number[], squaresOnly = false): WeightedFact[] {
  const bag: WeightedFact[] = [];
  const factors = tables.length ? tables : defaultTables();
  for (const a of factors) {
    for (let b = 1; b <= 12; b++) {
      if (squaresOnly && a !== b) continue;
      const hard = a >= 6 && b >= 6 && a !== 10 && b !== 10;
      bag.push({ a, b, weight: hard ? 1.35 : 1 });
    }
  }
  return bag.length ? bag : [{ a: 2, b: 2, weight: 1 }];
}

export function pickFact(
  bag: WeightedFact[],
  mastery: Map<string, Mastery>,
  rng = Math.random,
): { a: number; b: number } {
  let total = 0;
  const weighted = bag.map((fact) => {
    const key = `${fact.a}x${fact.b}`;
    const m = mastery.get(key);
    let w = fact.weight;
    if (m) {
      const missRate = m.seen === 0 ? 0 : m.misses / m.seen;
      w *= 1 + missRate * 2.4;
      if (m.bestMs > 1400) w *= 1.25;
    } else {
      w *= 1.15;
    }
    total += w;
    return w;
  });
  let cursor = rng() * total;
  for (let i = 0; i < bag.length; i++) {
    cursor -= weighted[i];
    if (cursor <= 0) return { a: bag[i].a, b: bag[i].b };
  }
  return bag[0];
}

export function makeQuestion(
  fact: { a: number; b: number },
  keys: string[],
  rng = Math.random,
): Question {
  const a = fact.a;
  const b = fact.b;
  const answer = a * b;
  const wrongs = shuffle(distractors(a, b, answer, rng), rng).slice(0, 3);
  while (wrongs.length < 3) {
    const extra = answer + wrongs.length + 2;
    if (!wrongs.includes(extra) && extra !== answer) wrongs.push(extra);
  }
  const values = shuffle([answer, ...wrongs.slice(0, 3)], rng);
  const choices: Choice[] = values.map((value, lane) => ({
    value,
    key: keys[lane],
    correct: value === answer,
    lane,
  }));
  return {
    a,
    b,
    prompt: `${a} × ${b}`,
    answer,
    choices,
  };
}

export function factKey(a: number, b: number): string {
  return `${a}x${b}`;
}

export function recordMastery(
  map: Map<string, Mastery>,
  a: number,
  b: number,
  hit: boolean,
  reactionMs: number,
): void {
  const key = factKey(a, b);
  const prev = map.get(key) ?? { seen: 0, hits: 0, misses: 0, bestMs: Number.POSITIVE_INFINITY };
  prev.seen += 1;
  if (hit) {
    prev.hits += 1;
    prev.bestMs = Math.min(prev.bestMs, reactionMs);
  } else {
    prev.misses += 1;
  }
  map.set(key, prev);
}

export const THREAT: Record<Difficulty, { hull: number; approachSec: number; label: string }> = {
  cadet: { hull: 5, approachSec: 7.4, label: "CADET" },
  pilot: { hull: 4, approachSec: 5.4, label: "PILOT" },
  ace: { hull: 3, approachSec: 3.7, label: "ACE" },
};
